/* eslint-disable @typescript-eslint/no-explicit-any */
import { AutoInject, Container } from 'vue-di';
import { Context } from '@nuxt/types';
import { configure } from '@trialcard/apigateway.client';
// special case to get access to the symbol. note the cjs
import { AXIOS } from '@trialcard/apigateway.client/cjs/symbols';
import { AXIOS_CONFIGURATION, X_TC_USERID } from '@trialcard/shared';
import axios, { AxiosRequestConfig, AxiosInstance, AxiosError } from 'axios';
import { IServiceContext, OAUTH_TOKEN_PROVIDER } from '@trialcard/authentication';
import VueRouter from 'vue-router';
import moment from 'moment';
import { Settings } from '~/services/settings';
import SessionModule from '~/store/session';

export default (context: Context) => {
    const container = context.container;

    // Get axios instance from Nuxt context or create new one
    let axiosInstance: AxiosInstance;

    if (process.client && context.app && (context.app as any).$axios) {
        axiosInstance = (context.app as any).$axios;
    } else {
        axiosInstance = axios.create({
            timeout: 30000,
        });
    }

    container.registerInstance(AXIOS, axiosInstance);

    container.registerInstance(Settings, new Settings(context.env));
    container.registerSingleton(OAUTH_TOKEN_PROVIDER, {
        get(container) {
            return new TokenProvider(container);
        },
    });

    container.registerInstance<AxiosRequestConfig>(AXIOS_CONFIGURATION, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        transformRequest (this: AxiosRequestConfig, data: any, headers?: any) {
            if (headers) {
                delete headers['User-Agent'];
            }

            const defaultTransformRequest = axios.defaults.transformRequest;

            if (Array.isArray(defaultTransformRequest)) {
                // If it's an array, use the first function
                return defaultTransformRequest[0] ? defaultTransformRequest[0].apply(this, [data, headers]) : data;
            } else if (typeof defaultTransformRequest === 'function') {
                // If it's a single function, call it directly
                return defaultTransformRequest.apply(this, [data, headers]);
            }

            return data;
        }
    });
    container.registerInstance<string>(X_TC_USERID, '');

    // Apply axios configuration to our instance
    const axiosConfig = container.get<AxiosRequestConfig>(AXIOS_CONFIGURATION);
    if (axiosConfig.transformRequest) {
        axiosInstance.defaults.transformRequest = axiosConfig.transformRequest as any;
    }

    // Now configure the API gateway with all dependencies properly registered
    configure(container, process.env.CONNECTTO as any);


    // Get the registered axios instance for interceptors
    const gatewayAxios = container.get<AxiosInstance>(AXIOS);
    gatewayAxios.interceptors.response.use(
        response => {
            return response;
        },
        async (error: AxiosError) => {
            const session = container.get(SessionModule);
            if (session.expirationDate && moment().isAfter(session.expirationDate)) {
                session.clearData();
                await session.ensure();
                context.app.router!.push({ name: 'index' });
            }
            return error;
        }
    );
    {
        // This is an IE cache busting interceptor
        let buster = 0;
        gatewayAxios.interceptors.request.use(
            request => {
                if (request.url && request.method && request.method.toLowerCase() === 'get') {
                    request.url += `${request.url.includes('?') ? '&' : '?'}__cachebust=${buster++}`;
                }
                return request;
            },
            e => e
        );
    }
    container.registerInstance(Settings, new Settings(context.env));
    container.registerResolver(VueRouter, {
        get() {
            return context.app.router;
        },
    });
};

@AutoInject
class TokenProvider {
    private _module?: SessionModule;
    public constructor(private readonly container: Container) {}
    public async getToken(context: IServiceContext) {
        if (!this._module) {
            this._module = this.container.get(SessionModule);
        }
        await this._module.ensure();
        return this._module.token;
    }
}
